import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "package-management-page" }
const _hoisted_2 = { class: "operation-bar" }
const _hoisted_3 = { class: "price-text" }
const _hoisted_4 = { class: "pagination-container" }
const _hoisted_5 = { class: "stat-item" }
const _hoisted_6 = { class: "stat-number" }
const _hoisted_7 = { class: "stat-item" }
const _hoisted_8 = { class: "stat-number" }
const _hoisted_9 = { class: "stat-item" }
const _hoisted_10 = { class: "stat-number" }
const _hoisted_11 = { class: "stat-item" }
const _hoisted_12 = { class: "stat-number" }
const _hoisted_13 = { class: "dialog-footer" }

import { ref, reactive, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { ElMessage, ElMessageBox } from "element-plus";
import { Plus, Refresh } from "@element-plus/icons-vue";
import type { FormInstance, FormRules } from "element-plus";
import apiClient from "@/services/apiClient";
import config from "@/config";

// 路由和状态管理

export default /*@__PURE__*/_defineComponent({
  __name: 'PackageManagement',
  setup(__props) {

const router = useRouter();
const store = useStore();

// 响应式数据
const loading = ref(false);
const tableLoading = ref(false);
const submitLoading = ref(false);
const dialogVisible = ref(false);
const isEdit = ref(false);
const packages = ref([]);
const statistics = ref<any>({
  total_packages: 0,
  active_packages: 0,
  total_devices: 0,
  packages_with_devices: 0,
});
const currentPage = ref(1);
const pageSize = ref(20);
const total = ref(0);

// 表单相关
const packageFormRef = ref<FormInstance>();
const packageForm = reactive({
  id: null,
  name: "",
  monthly_free_minutes: 30,
  recording_retention_days: 30,
  price: 0.0,
  description: "",
  is_active: true,
});

// 表单验证规则
const packageRules: FormRules = {
  name: [
    { required: true, message: "请输入套餐名称", trigger: "blur" },
    {
      min: 2,
      max: 100,
      message: "套餐名称长度在 2 到 100 个字符",
      trigger: "blur",
    },
  ],
  monthly_free_minutes: [
    { required: true, message: "请输入每月免费通话时间", trigger: "blur" },
    { type: "number", min: 1, message: "通话时间必须大于0", trigger: "blur" },
  ],
  recording_retention_days: [
    { required: true, message: "请输入录音保存天数", trigger: "blur" },
    {
      type: "number",
      min: 1,
      max: 365,
      message: "保存天数在 1 到 365 天之间",
      trigger: "blur",
    },
  ],
  price: [
    { required: true, message: "请输入价格", trigger: "blur" },
    { type: "number", min: 0, message: "价格不能为负数", trigger: "blur" },
  ],
};

// 计算属性
const dialogTitle = computed(() => (isEdit.value ? "编辑套餐" : "新增套餐"));

// 权限检查
const hasPermission = (permission: string): boolean => {
  const userRole = store.getters.getUserRole;
  const permissions = store.getters.getUserPermissions || [];
  return permissions.includes(permission) || userRole === "super_admin";
};

// 返回上一页
const goBack = () => {
  router.go(-1);
};

// 格式化日期时间
const formatDateTime = (dateTime: string) => {
  if (!dateTime) return "N/A";
  return new Date(dateTime).toLocaleString("zh-CN");
};

// 获取套餐列表
const fetchPackages = async () => {
  try {
    tableLoading.value = true;
    const response = await apiClient.get(`${config.apiBaseUrl}/packages`, {
      params: {
        skip: (currentPage.value - 1) * pageSize.value,
        limit: pageSize.value,
      },
    });

    if (response.data.code === 0) {
      packages.value = response.data.data.items || [];
      total.value = response.data.data.total || 0;
    } else {
      ElMessage.error(response.data.message || "获取套餐列表失败");
    }
  } catch (error) {
    console.error("获取套餐列表失败:", error);
    ElMessage.error("获取套餐列表失败");
  } finally {
    tableLoading.value = false;
  }
};

// 获取套餐统计信息
const fetchStatistics = async () => {
  try {
    const response = await apiClient.get(
      `${config.apiBaseUrl}/packages/statistics`
    );
    if (response.data.code === 0) {
      statistics.value = response.data.data;
    } else {
      console.error("获取套餐统计失败:", response.data.message);
    }
  } catch (error) {
    console.error("获取套餐统计失败:", error);
  }
};

// 刷新数据
const refreshData = () => {
  fetchPackages();
  fetchStatistics();
};

// 显示新增对话框
const showCreateDialog = () => {
  isEdit.value = false;
  resetForm();
  dialogVisible.value = true;
};

// 显示编辑对话框
const showEditDialog = (pkg: any) => {
  isEdit.value = true;
  Object.assign(packageForm, pkg);
  dialogVisible.value = true;
};

// 重置表单
const resetForm = () => {
  Object.assign(packageForm, {
    id: null,
    name: "",
    monthly_free_minutes: 30,
    recording_retention_days: 30,
    price: 0.0,
    description: "",
    is_active: true,
  });
  packageFormRef.value?.clearValidate();
};

// 提交表单
const handleSubmit = async () => {
  if (!packageFormRef.value) return;

  try {
    await packageFormRef.value.validate();
    submitLoading.value = true;

    let response;
    if (isEdit.value) {
      response = await apiClient.put(
        `${config.apiBaseUrl}/packages/${packageForm.id}`,
        packageForm
      );
    } else {
      response = await apiClient.post(
        `${config.apiBaseUrl}/packages`,
        packageForm
      );
    }

    if (response.data.code === 0) {
      ElMessage.success(isEdit.value ? "套餐更新成功" : "套餐创建成功");
      dialogVisible.value = false;
      refreshData();
    } else {
      ElMessage.error(response.data.message || "操作失败");
    }
  } catch (error) {
    console.error("提交失败:", error);
    ElMessage.error("操作失败");
  } finally {
    submitLoading.value = false;
  }
};

// 删除套餐
const handleDelete = async (pkg: any) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除套餐"${pkg.name}"吗？此操作不可恢复。`,
      "确认删除",
      {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }
    );

    const response = await apiClient.delete(
      `${config.apiBaseUrl}/packages/${pkg.id}`
    );
    if (response.data.code === 0) {
      ElMessage.success("套餐删除成功");
      refreshData();
    } else {
      ElMessage.error(response.data.message || "删除失败");
    }
  } catch (error) {
    if (error !== "cancel") {
      console.error("删除失败:", error);
      ElMessage.error("删除失败");
    }
  }
};

// 分页处理
const handleSizeChange = (val: number) => {
  pageSize.value = val;
  currentPage.value = 1;
  fetchPackages();
};

const handleCurrentChange = (val: number) => {
  currentPage.value = val;
  fetchPackages();
};

// 页面加载
onMounted(() => {
  refreshData();
});

return (_ctx: any,_cache: any) => {
  const _component_el_page_header = _resolveComponent("el-page-header")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_input_number = _resolveComponent("el-input-number")!
  const _component_el_switch = _resolveComponent("el-switch")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_el_page_header, {
      onBack: goBack,
      class: "page-header"
    }, {
      content: _withCtx(() => [...(_cache[10] || (_cache[10] = [
        _createElementVNode("span", { class: "text-large font-600 mr-3" }, "套餐管理", -1)
      ]))]),
      _: 1
    }),
    _createVNode(_component_el_card, { class: "operation-card" }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          (hasPermission('package.manage'))
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 0,
                type: "primary",
                onClick: showCreateDialog
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_icon, null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Plus))
                    ]),
                    _: 1
                  }),
                  _cache[11] || (_cache[11] = _createTextVNode(" 新增套餐 ", -1))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(_component_el_button, { onClick: refreshData }, {
            default: _withCtx(() => [
              _createVNode(_component_el_icon, null, {
                default: _withCtx(() => [
                  _createVNode(_unref(Refresh))
                ]),
                _: 1
              }),
              _cache[12] || (_cache[12] = _createTextVNode(" 刷新 ", -1))
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_el_card, { class: "package-list-card" }, {
      default: _withCtx(() => [
        _withDirectives((_openBlock(), _createBlock(_component_el_table, {
          data: packages.value,
          stripe: "",
          style: {"width":"100%"}
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_table_column, {
              prop: "id",
              label: "ID",
              width: "80"
            }),
            _createVNode(_component_el_table_column, {
              prop: "name",
              label: "套餐名称",
              width: "150"
            }),
            _createVNode(_component_el_table_column, {
              prop: "monthly_free_minutes",
              label: "每月免费通话时间",
              width: "180"
            }, {
              default: _withCtx((scope) => [
                _createVNode(_component_el_tag, { type: "success" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(scope.row.monthly_free_minutes) + " 分钟", 1)
                  ]),
                  _: 2
                }, 1024)
              ]),
              _: 1
            }),
            _createVNode(_component_el_table_column, {
              prop: "recording_retention_days",
              label: "录音保存天数",
              width: "150"
            }, {
              default: _withCtx((scope) => [
                _createVNode(_component_el_tag, { type: "info" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(scope.row.recording_retention_days) + " 天", 1)
                  ]),
                  _: 2
                }, 1024)
              ]),
              _: 1
            }),
            _createVNode(_component_el_table_column, {
              prop: "price",
              label: "价格",
              width: "120"
            }, {
              default: _withCtx((scope) => [
                _createElementVNode("span", _hoisted_3, "¥" + _toDisplayString(scope.row.price), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_el_table_column, {
              prop: "description",
              label: "描述",
              "min-width": "200",
              "show-overflow-tooltip": ""
            }),
            _createVNode(_component_el_table_column, {
              prop: "is_active",
              label: "状态",
              width: "100"
            }, {
              default: _withCtx((scope) => [
                _createVNode(_component_el_tag, {
                  type: scope.row.is_active ? 'success' : 'danger'
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(scope.row.is_active ? "启用" : "禁用"), 1)
                  ]),
                  _: 2
                }, 1032, ["type"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_table_column, {
              prop: "created_at",
              label: "创建时间",
              width: "180"
            }, {
              default: _withCtx((scope) => [
                _createTextVNode(_toDisplayString(formatDateTime(scope.row.created_at)), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_el_table_column, {
              label: "操作",
              width: "200",
              fixed: "right"
            }, {
              default: _withCtx((scope) => [
                (hasPermission('package.manage'))
                  ? (_openBlock(), _createBlock(_component_el_button, {
                      key: 0,
                      size: "small",
                      type: "primary",
                      onClick: ($event: any) => (showEditDialog(scope.row))
                    }, {
                      default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                        _createTextVNode(" 编辑 ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["onClick"]))
                  : _createCommentVNode("", true),
                (hasPermission('package.manage'))
                  ? (_openBlock(), _createBlock(_component_el_button, {
                      key: 1,
                      size: "small",
                      type: "danger",
                      onClick: ($event: any) => (handleDelete(scope.row))
                    }, {
                      default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                        _createTextVNode(" 删除 ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["onClick"]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["data"])), [
          [_directive_loading, tableLoading.value]
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_el_pagination, {
            "current-page": currentPage.value,
            "onUpdate:currentPage": _cache[0] || (_cache[0] = ($event: any) => ((currentPage).value = $event)),
            "page-size": pageSize.value,
            "onUpdate:pageSize": _cache[1] || (_cache[1] = ($event: any) => ((pageSize).value = $event)),
            "page-sizes": [10, 20, 50, 100],
            total: total.value,
            layout: "total, sizes, prev, pager, next, jumper",
            onSizeChange: handleSizeChange,
            onCurrentChange: handleCurrentChange
          }, null, 8, ["current-page", "page-size", "total"])
        ])
      ]),
      _: 1
    }),
    (statistics.value)
      ? (_openBlock(), _createBlock(_component_el_card, {
          key: 0,
          class: "statistics-card"
        }, {
          header: _withCtx(() => [...(_cache[15] || (_cache[15] = [
            _createElementVNode("span", null, "套餐使用统计", -1)
          ]))]),
          default: _withCtx(() => [
            _createVNode(_component_el_row, { gutter: 20 }, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 6 }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("div", _hoisted_6, _toDisplayString(statistics.value.total_packages), 1),
                      _cache[16] || (_cache[16] = _createElementVNode("div", { class: "stat-label" }, "总套餐数", -1))
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_col, { span: 6 }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_7, [
                      _createElementVNode("div", _hoisted_8, _toDisplayString(statistics.value.active_packages), 1),
                      _cache[17] || (_cache[17] = _createElementVNode("div", { class: "stat-label" }, "启用套餐数", -1))
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_col, { span: 6 }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("div", _hoisted_10, _toDisplayString(statistics.value.total_devices), 1),
                      _cache[18] || (_cache[18] = _createElementVNode("div", { class: "stat-label" }, "使用设备数", -1))
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_col, { span: 6 }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_11, [
                      _createElementVNode("div", _hoisted_12, _toDisplayString(statistics.value.packages_with_devices), 1),
                      _cache[19] || (_cache[19] = _createElementVNode("div", { class: "stat-label" }, "有设备套餐数", -1))
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_component_el_dialog, {
      modelValue: dialogVisible.value,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((dialogVisible).value = $event)),
      title: dialogTitle.value,
      width: "600px",
      "close-on-click-modal": false
    }, {
      footer: _withCtx(() => [
        _createElementVNode("span", _hoisted_13, [
          _createVNode(_component_el_button, {
            onClick: _cache[8] || (_cache[8] = ($event: any) => (dialogVisible.value = false))
          }, {
            default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
              _createTextVNode("取消", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_el_button, {
            type: "primary",
            onClick: handleSubmit,
            loading: submitLoading.value
          }, {
            default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
              _createTextVNode(" 确定 ", -1)
            ]))]),
            _: 1
          }, 8, ["loading"])
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_form, {
          ref_key: "packageFormRef",
          ref: packageFormRef,
          model: packageForm,
          rules: packageRules,
          "label-width": "140px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, {
              label: "套餐名称",
              prop: "name"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: packageForm.name,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((packageForm.name) = $event)),
                  placeholder: "请输入套餐名称"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "每月免费通话时间",
              prop: "monthly_free_minutes"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input_number, {
                  modelValue: packageForm.monthly_free_minutes,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((packageForm.monthly_free_minutes) = $event)),
                  min: 1,
                  max: 9999,
                  placeholder: "分钟"
                }, null, 8, ["modelValue"]),
                _cache[20] || (_cache[20] = _createElementVNode("span", { class: "form-hint" }, "分钟", -1))
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "录音保存天数",
              prop: "recording_retention_days"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input_number, {
                  modelValue: packageForm.recording_retention_days,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((packageForm.recording_retention_days) = $event)),
                  min: 1,
                  max: 365,
                  placeholder: "天数"
                }, null, 8, ["modelValue"]),
                _cache[21] || (_cache[21] = _createElementVNode("span", { class: "form-hint" }, "天", -1))
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "价格",
              prop: "price"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input_number, {
                  modelValue: packageForm.price,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((packageForm.price) = $event)),
                  min: 0,
                  precision: 2,
                  step: 0.01,
                  placeholder: "价格"
                }, null, 8, ["modelValue"]),
                _cache[22] || (_cache[22] = _createElementVNode("span", { class: "form-hint" }, "元", -1))
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "描述",
              prop: "description"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: packageForm.description,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((packageForm.description) = $event)),
                  type: "textarea",
                  rows: 3,
                  placeholder: "请输入套餐描述"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "状态",
              prop: "is_active"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_switch, {
                  modelValue: packageForm.is_active,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((packageForm.is_active) = $event)),
                  "active-text": "启用",
                  "inactive-text": "禁用"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ])), [
    [_directive_loading, loading.value]
  ])
}
}

})